import { useMemo } from 'react'

import type { CalendarItemBundleData, DateRange } from './types'

interface Params {
  infiniteMemberOnly: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  /**
   * Returns whether a date carries a bookable Infinite Member (Friends & Family) price.
   * Pass `dateHasInfiniteMemberPrice` (which checks every cash cabin) so this matches the
   * per-cabin badge exactly — not the cheapest-bundle aggregate `isFnfPriceApplied`.
   */
  isDateInfiniteEligible: (checkIn: string) => boolean
}

/**
 * Narrows the per-month charter date ranges to those eligible for the "Infinite
 * Member Price" filter. When the filter is off, the input is returned unchanged.
 *
 * While a date's bundle pricing is still fetching, the date is kept so the
 * calendar doesn't flash its empty state before pricing resolves — e.g. when
 * landing with `?infiniteMember=true` already in the URL, the placeholder entries
 * from `useMaldivesDirectCalendarPricing` haven't loaded yet. This mirrors the
 * `rewardSeatsOnly` loading guard in `MaldivesCharterTripOptionsTable`.
 */
export function useEligibleDateRangesByMonth({
  infiniteMemberOnly,
  dateRangesByMonth,
  itemBundleDataByDate,
  isDateInfiniteEligible,
}: Params): Record<string, Array<DateRange>> {
  return useMemo(() => {
    if (!infiniteMemberOnly) {
      return dateRangesByMonth
    }
    const filtered: Record<string, Array<DateRange>> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      const eligible = dateRanges.filter(({ checkIn }) => {
        const entry = itemBundleDataByDate?.[checkIn]
        if (entry?.fetching) return true
        return isDateInfiniteEligible(checkIn)
      })
      if (eligible.length > 0) {
        filtered[monthName] = eligible
      }
    })
    return filtered
  }, [infiniteMemberOnly, dateRangesByMonth, itemBundleDataByDate, isDateInfiniteEligible])
}
